/**
 * `mockData.json` sidecar: client-only mock state that doesn't belong in the
 * workflow JSON definition (the runtime contract), but does need to survive
 * across devices and across page reloads.
 *
 * Why a sidecar instead of fields in workflow.json?
 *   - Workflow JSON stays canonical: mock outputs and toggles never leak into
 *     the saved definition (comparing definitions doesn't flip on mock edits).
 *   - The runtime ignores unknown data, but other tooling (validators, CI
 *     diffs, hand-edits) shouldn't have to know what to skip.
 *
 * Draft-only:
 *   - Unlike `nodesMetadata.json`, this file is FLAT — there is no
 *     `draft`/`published` split. Mock data exists only for the draft. The
 *     published view has no mocks at all: switching to published clears the
 *     in-memory slice and this sidecar is never read/applied in that mode.
 *
 * Source of truth at runtime: the `mockedNodes` / `mockRunEnabled` slice on
 * `workflowStore`. This sidecar is the *persistence* layer for that slice.
 *
 * Round-trip:
 *   1. Load: fetch sidecar → `hydrateMocks(file.mockedNodes, file.mockRunEnabled)`
 *      seeds the store (draft mode only) without marking the workflow dirty.
 *   2. Save: `buildMockData` reads the store slice → JSON. User-facing mock
 *      edits mark the workflow dirty so the draft save PUTs this alongside
 *      workflow-draft.json and nodesMetadata.json.
 */

import type { MockNodeInfo, TriggerMockPayload } from '@/types/templateMode';

/** Current sidecar schema version. Bump when the on-disk shape changes. */
export const MOCK_DATA_VERSION = 1;

/** Mirrors the `mockedNodes` slice: keyed by `trigger-{name}` / `action-{scopedPath}`. */
export type MockedNodesMap = Record<string, MockNodeInfo | TriggerMockPayload>;

export interface MockDataFile {
  version: typeof MOCK_DATA_VERSION;
  updatedAt: number;
  mockedNodes: MockedNodesMap;
  mockRunEnabled: boolean;
}

export function emptyMockData(): MockDataFile {
  return {
    version: MOCK_DATA_VERSION,
    updatedAt: 0,
    mockedNodes: {},
    mockRunEnabled: false,
  };
}

/**
 * Build the sidecar file content from the current store slice. `updatedAt`
 * stamps the write so the empty-file bailout in the api layer can distinguish
 * a never-populated sidecar from a real one.
 */
export function buildMockData(
  mockedNodes: MockedNodesMap,
  mockRunEnabled: boolean,
): MockDataFile {
  return {
    version: MOCK_DATA_VERSION,
    updatedAt: Date.now(),
    mockedNodes: { ...mockedNodes },
    mockRunEnabled,
  };
}

/**
 * Parse a raw sidecar value (e.g. a JSON.parse'd HTTP body) into a typed file.
 * Returns `emptyMockData()` for any unrecognized shape or version mismatch.
 * Mock entry contents are passed through as-is (the store-side types own their
 * own shape); we only validate the envelope.
 */
export function parseMockData(raw: unknown): MockDataFile {
  if (typeof raw !== 'object' || raw === null) return emptyMockData();
  const obj = raw as Record<string, unknown>;
  if (obj.version !== MOCK_DATA_VERSION) return emptyMockData();
  const mockedNodes =
    typeof obj.mockedNodes === 'object' && obj.mockedNodes !== null
      ? (obj.mockedNodes as MockedNodesMap)
      : {};
  return {
    version: MOCK_DATA_VERSION,
    updatedAt: typeof obj.updatedAt === 'number' ? obj.updatedAt : 0,
    mockedNodes,
    mockRunEnabled: obj.mockRunEnabled === true,
  };
}

/** True when the file carries no persisted mock state worth applying. */
export function isEmptyMockData(file: MockDataFile): boolean {
  return (
    file.updatedAt === 0 &&
    Object.keys(file.mockedNodes).length === 0 &&
    !file.mockRunEnabled
  );
}
